import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Product } from '../types';
import { getImageUrl, imageExists } from '../utils/imageUtils';
import { getProductApiUrl } from '../config';
import styles from './ProductDetailPage.module.css';

interface ProductDetailPageProps {
  onAddToCart: (product: Product, size: string) => void;
}

const ProductDetailPage: React.FC<ProductDetailPageProps> = ({
  onAddToCart,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    shipping: false,
    washing: false,
    contact: false,
  });
  const backButtonRef = useRef<HTMLDivElement>(null);

  console.log('🔍 ProductDetailPage mounted with ID:', id);

  const ImageWithFallback = useCallback(({ 
    src, 
    alt, 
    className 
  }: { 
    src: string; 
    alt: string; 
    className: string 
  }) => {
    const [imgSrc, setImgSrc] = useState(getImageUrl(src));
    const [errored, setErrored] = useState(false);
  
    // Check if image exists when component mounts or src changes
    useEffect(() => {
      let isMounted = true;
      
      const checkImage = async () => {
        const exists = await imageExists(imgSrc);
        if (!exists && isMounted) {
          setImgSrc('/images/placeholder.png');
          setErrored(true);
        }
      };
  
      if (!errored) {
        checkImage();
      }
  
      return () => {
        isMounted = false;
      };
    }, [src, imgSrc, errored]);
  
    const handleError = () => {
      if (!errored) {
        setImgSrc('/images/placeholder.png');
        setErrored(true);
      }
    };
  
    return (
      <img
        src={imgSrc}
        alt={alt}
        className={className}
        onError={handleError}
      />
    );
  }, []);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;

      const apiUrl = getProductApiUrl(id);
      console.log('🔍 Fetching product from:', apiUrl);
      console.log('🔍 Product ID:', id);

      try {
        setLoading(true);
        const response = await fetch(apiUrl, {
          headers: {
            Accept: 'application/json',
          },
          mode: 'cors',
        });

        console.log('🔍 Response status:', response.status);
        console.log(
          '🔍 Response headers:',
          Object.fromEntries(response.headers.entries())
        );

        if (!response.ok) {
          throw new Error(
            `Failed to fetch product: ${response.status} ${response.statusText}`
          );
        }

        const data = await response.json();
        console.log('🔍 Product data received:', data);
        setProduct(data);
      } catch (err) {
        console.error('Error fetching product:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to fetch product'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  useEffect(() => {
    if (backButtonRef.current) {
      backButtonRef.current.style.setProperty('color', '#6B7280', 'important');
    }
  }, []);

  const handleAddToCart = () => {
    if (product) {
      // If product has multiple sizes, require size selection
      if (product.sizes && product.sizes.length > 1 && !selectedSize) {
        return; // Don't add to cart if size is required but not selected
      }

      // For single size products, use the available size
      const sizeToUse =
        product.sizes && product.sizes.length === 1
          ? product.sizes[0]
          : selectedSize || '';

      // Add to cart with or without size
      onAddToCart(product, sizeToUse);
      navigate('/cart');
    }
  };

  const toggleAccordion = (section: 'shipping' | 'washing' | 'contact') => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingContent}>
          <div className={styles.loadingSpinner}></div>
          <p className={styles.loadingText}>Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.errorContent}>
          <h1 className={styles.errorTitle}>Product Not Found</h1>
          <p className={styles.errorMessage}>
            {error || 'The product you are looking for does not exist.'}
          </p>
          <Link to="/products" className={styles.backToProductsButton}>
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.productDetailPage}>
      <div className={styles.container}>
        {/* Back to Products */}
        <div className={styles.backButton}>
          <div
            ref={backButtonRef}
            onClick={() => navigate('/products')}
            className={styles.backButtonContent}
          >
            <svg
              className={styles.backIcon}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            Back to Products
          </div>
        </div>

        {/* Product Details */}
        <div className={styles.productContainer}>
          <div className={styles.productGrid}>
            {/* Product Images - Left Side (2/3 width) */}
            <div className={styles.productImages}>
              <div className={styles.imageGrid}>
                {product.images && product.images.length > 0 ? (
                  product.images.map((image, index) => (
                    <ImageWithFallback
                      key={index}
                      src={getImageUrl(image)}
                      alt={`${product.name} - ${index + 1}`}
                      className={styles.productImage}
                    />
                  ))
                ) : (
                  // Fallback to single image if no additional images
                  <>
                    <ImageWithFallback
                      src={getImageUrl(product.image || product.imageUrl)}
                      alt={product.name}
                      className={styles.fallbackImage}
                    />
                    <ImageWithFallback
                      src={getImageUrl(product.image || product.imageUrl)}
                      alt={product.name}
                      className={styles.fallbackImage}
                    />
                    <ImageWithFallback
                      src={getImageUrl(product.image || product.imageUrl)}
                      alt={product.name}
                      className={styles.fallbackImage}
                    />
                    <ImageWithFallback
                      src={getImageUrl(product.image || product.imageUrl)}
                      alt={product.name}
                      className={styles.fallbackImage}
                    />
                  </>
                )}
              </div>
            </div>

            {/* Product Information - Right Side (1/3 width) */}
            <div className={styles.productInfo}>
              {/* Title */}
              <div>
                <h1 className={styles.productTitle}>{product.name}</h1>
              </div>

              {/* Price */}
              <div>
                <p className={styles.productPrice}>
                  ${product.price ? product.price.toFixed(2) : 'N/A'}
                </p>
              </div>

              {/* Shipping Disclaimer */}
              <div className={styles.shippingInfo}>
                <p className={styles.shippingText}>
                  <span className={styles.shippingLabel}>Shipping:</span>{' '}
                  Calculated at checkout
                </p>
              </div>

              {/* Size Selector - Only show if product has multiple sizes */}
              {product.sizes && product.sizes.length > 1 && (
                <div className={styles.sizeSelector}>
                  <label htmlFor="size" className={styles.sizeLabel}>
                    Size: *
                  </label>
                  <select
                    id="size"
                    value={selectedSize}
                    onChange={e => setSelectedSize(e.target.value)}
                    className={styles.sizeSelect}
                  >
                    <option value="">Select Size: </option>
                    {product.sizes.map(size => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Show single size info if only one size available */}
              {product.sizes && product.sizes.length === 1 && (
                <div className={styles.sizeInfo}>
                  <span className={styles.sizeLabel}>Size:</span>
                  <span className={styles.singleSize}>{product.sizes[0]}</span>
                </div>
              )}

              {/* Quantity Selector */}
              <div className={styles.quantitySelector}>
                <label htmlFor="quantity" className={styles.quantityLabel}>
                  Quantity:{' '}
                </label>
                <select
                  id="quantity"
                  value={quantity}
                  onChange={e => setQuantity(Number(e.target.value))}
                  className={styles.quantitySelect}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
              </div>

              {/* Add to Cart Button */}
              <button
                onClick={handleAddToCart}
                disabled={
                  product.sizes && product.sizes.length > 1 && !selectedSize
                }
                className={`${styles.addToCartButton} ${
                  !(product.sizes && product.sizes.length > 1 && !selectedSize)
                    ? styles.addToCartButtonEnabled
                    : styles.addToCartButtonDisabled
                }`}
              >
                {!(product.sizes && product.sizes.length > 1 && !selectedSize)
                  ? `Add to Cart - $${
                      product.price
                        ? (product.price * quantity).toFixed(2)
                        : 'N/A'
                    }`
                  : 'Please Select a Size'}
              </button>

              {/* Product Details */}
              <div className={styles.productDetails}>
                <h3 className={styles.productDetailsTitle}>Product Details</h3>
                <div className={styles.productDetailsContent}>
                  <p className={styles.productDetailItem}>
                    <span className={styles.productDetailLabel}>
                      Availability:
                    </span>{' '}
                    {product.availability || 'In Stock'}
                  </p>
                  {product.description && (
                    <p className={styles.productDetailItem}>
                      <span className={styles.productDetailLabel}>
                        Description:
                      </span>{' '}
                      {product.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Accordion Section */}
        <div className={styles.accordionContainer}>
          <div className={styles.accordionItem}>
            <button
              className={styles.accordionHeader}
              onClick={() => toggleAccordion('shipping')}
            >
              <span>Shipping Policy</span>
              <svg
                className={`${styles.accordionIcon} ${
                  expandedSections.shipping ? styles.expanded : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`${styles.accordionContent} ${
                expandedSections.shipping ? styles.expanded : ''
              }`}
            >
              <div className={styles.accordionText}>
                <p>
                  Please note that all of our products are made to order. This
                  means that we begin production only after the website has
                  officially closed for orders. Unlike ready-to-ship items,
                  made-to-order products require time to ensure the highest
                  quality, accuracy, and craftsmanship.
                </p>

                <p>
                  <strong>Production Timeline</strong>
                </p>

                <p>
                  Once the website closes and all orders are finalized,
                  production will begin. From that point, please allow up to 2
                  to 3 weeks for your order to be produced. This timeframe
                  includes material sourcing (if needed), printing or
                  manufacturing, quality checks, and packaging preparation. We
                  are committed to ensuring that each product meets our
                  standards before it leaves our hands.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.accordionItem}>
            <button
              className={styles.accordionHeader}
              onClick={() => toggleAccordion('washing')}
            >
              <span>Washing Instructions</span>
              <svg
                className={`${styles.accordionIcon} ${
                  expandedSections.washing ? styles.expanded : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`${styles.accordionContent} ${
                expandedSections.washing ? styles.expanded : ''
              }`}
            >
              <div className={styles.accordionText}>
                <p>
                  <strong>Washing Instructions</strong>
                </p>

                <p>
                  To keep your garments looking their best, hand wash only in
                  cold water and lay flat to dry. Avoid wringing, twisting, or
                  using a washing machine, as this may loosen or damage the
                  clothing. Do not bleach or tumble dry.
                </p>
              </div>
            </div>
          </div>

          <div className={styles.accordionItem}>
            <button
              className={styles.accordionHeader}
              onClick={() => toggleAccordion('contact')}
            >
              <span>Contact Information</span>
              <svg
                className={`${styles.accordionIcon} ${
                  expandedSections.contact ? styles.expanded : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            <div
              className={`${styles.accordionContent} ${
                expandedSections.contact ? styles.expanded : ''
              }`}
            >
              <div className={styles.accordionText}>
                <p>
                  <strong>Contact Us</strong>
                </p>

                <p>
                  Thank you for being part of the Project Michael community!
                  Whether you have a question about your order, need more
                  information about our products, or just want to connect—we'd
                  love to hear from you.
                </p>

                <p>
                  Please don't hesitate to reach out to us at{' '}
                  <strong>tybierwagen@gmail.com</strong>. A member of our team will
                  get back to you as soon as possible, typically within 1-2
                  business days.
                </p>

                <p>
                  We appreciate your support and interest in Project Michael,
                  and we're always here to help make your experience as smooth
                  as possible.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
